/**
 * Starfield - Fond étoilé animé performant et autonome
 * --------------------------------------------------------
 * Usage vanilla :
 *   const sf = createStarfield({
 *     container: document.body, // ou n'importe quel élément
 *     starCount: 275,           // nombre d'étoiles (200-300 recommandé)
 *     opacity: 1,               // intensité globale (0 à 1)
 *     shootingStarFrequency: 1, // 0 = aucune, 1 = normal, 2 = fréquent
 *   });
 *   // Pour arrêter / nettoyer :
 *   sf.destroy();
 *
 * Caractéristiques :
 * - z-index -1, pointer-events none -> ne bloque jamais les clics ni le texte
 * - position fixed, plein écran, responsive (resize natif)
 * - Pause automatique quand l'onglet est en arrière-plan (économie CPU)
 * - DPR-aware (rendu net sur écrans Retina)
 * - Twinkle naturel, étoiles filantes rares et élégantes
 */

export function createStarfield(options = {}) {
  const {
    container = document.body,
    starCount = 275,
    opacity = 1,
    shootingStarFrequency = 1,
    zIndex = -1,
  } = options;

  // --- Création du canvas ---
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  Object.assign(canvas.style, {
    position: "fixed",
    inset: "0",
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: String(zIndex),
    display: "block",
    background: "transparent",
  });
  container.appendChild(canvas);

  const ctx = canvas.getContext("2d", { alpha: true });
  let width = 0;
  let height = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2); // cap à 2 pour la perf

  // --- Données ---
  let stars = [];
  const shootingStars = [];
  let rafId = null;
  let lastTime = performance.now();
  let running = true;

  // --- Helpers ---
  const rand = (min, max) => min + Math.random() * (max - min);

  function createStar() {
    // Distribution : majorité petites, quelques moyennes, très rares "brillantes"
    const r = Math.random();
    let radius;
    if (r < 0.78) radius = rand(0.25, 0.7);
    else if (r < 0.97) radius = rand(0.7, 1.2);
    else radius = rand(1.2, 1.7); // étoiles plus marquées (rares)

    return {
      x: Math.random() * width,
      y: Math.random() * height,
      r: radius,
      baseAlpha: rand(0.35, 0.95),
      twinkleAmp: rand(0.15, 0.45),
      twinkleSpeed: rand(0.4, 1.6), // rad/s
      phase: Math.random() * Math.PI * 2,
      // Drift très lent (px/s) - effet "espace"
      vx: rand(-4, 4) / 100, // ~0.04 px/s
      vy: rand(-3, 3) / 100,
    };
  }

  function spawnShootingStar() {
    // Apparition depuis le bord supérieur, trajectoire diagonale descendante
    const fromLeft = Math.random() < 0.5;
    const startX = fromLeft ? rand(-50, width * 0.4) : rand(width * 0.6, width + 50);
    const startY = rand(-20, height * 0.35);
    const angle = fromLeft ? rand(Math.PI / 6, Math.PI / 3) : rand(Math.PI - Math.PI / 3, Math.PI - Math.PI / 6);
    const speed = rand(550, 850); // px/s
    shootingStars.push({
      x: startX,
      y: startY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life: 0,
      maxLife: rand(0.6, 1.0), // secondes
      tailLength: rand(120, 200),
    });
  }

  // --- Resize ---
  function resize() {
    width = window.innerWidth;
    height = window.innerHeight;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // Régénère si pas encore d'étoiles, sinon reprojette en évitant la perte
    if (stars.length === 0) {
      stars = Array.from({ length: starCount }, createStar);
    } else {
      // Replace les étoiles qui sont sorties du nouveau canvas
      for (const s of stars) {
        if (s.x > width) s.x = Math.random() * width;
        if (s.y > height) s.y = Math.random() * height;
      }
    }
  }

  // --- Boucle d'animation ---
  function tick(now) {
    if (!running) return;
    const dt = Math.min((now - lastTime) / 1000, 0.05); // cap dt à 50ms pour éviter les sauts
    lastTime = now;

    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = 1;

    // --- Étoiles fixes (twinkle + drift lent) ---
    for (let i = 0; i < stars.length; i++) {
      const s = stars[i];
      s.phase += s.twinkleSpeed * dt;
      s.x += s.vx;
      s.y += s.vy;

      // Wrap autour de l'écran
      if (s.x < -2) s.x = width + 2;
      else if (s.x > width + 2) s.x = -2;
      if (s.y < -2) s.y = height + 2;
      else if (s.y > height + 2) s.y = -2;

      const alpha = Math.max(
        0,
        Math.min(1, (s.baseAlpha + Math.sin(s.phase) * s.twinkleAmp) * opacity)
      );

      ctx.beginPath();
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();

      // Léger halo pour les rares étoiles plus grosses
      if (s.r > 1.15) {
        ctx.beginPath();
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.12})`;
        ctx.arc(s.x, s.y, s.r * 2.8, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // --- Spawn étoiles filantes (rares) ---
    if (shootingStarFrequency > 0) {
      // Probabilité ~ 1 toutes les 8-12s en mode normal
      const baseChance = 0.0018 * shootingStarFrequency;
      if (Math.random() < baseChance && shootingStars.length < 3) {
        spawnShootingStar();
      }
    }

    // --- Dessin étoiles filantes ---
    for (let i = shootingStars.length - 1; i >= 0; i--) {
      const sh = shootingStars[i];
      sh.life += dt;
      sh.x += sh.vx * dt;
      sh.y += sh.vy * dt;

      const lifeRatio = sh.life / sh.maxLife;
      if (lifeRatio >= 1 || sh.x < -250 || sh.x > width + 250 || sh.y > height + 250) {
        shootingStars.splice(i, 1);
        continue;
      }

      // Fade-in puis fade-out subtil
      const fade = lifeRatio < 0.2
        ? lifeRatio / 0.2
        : 1 - (lifeRatio - 0.2) / 0.8;
      const a = Math.max(0, Math.min(1, fade * 0.85 * opacity));

      // Queue dégradée
      const tailX = sh.x - (sh.vx / Math.hypot(sh.vx, sh.vy)) * sh.tailLength;
      const tailY = sh.y - (sh.vy / Math.hypot(sh.vx, sh.vy)) * sh.tailLength;

      const grad = ctx.createLinearGradient(sh.x, sh.y, tailX, tailY);
      grad.addColorStop(0, `rgba(255,255,255,${a})`);
      grad.addColorStop(0.4, `rgba(255,255,255,${a * 0.35})`);
      grad.addColorStop(1, "rgba(255,255,255,0)");

      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.4;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(sh.x, sh.y);
      ctx.stroke();

      // Tête lumineuse
      ctx.beginPath();
      ctx.fillStyle = `rgba(255,255,255,${a})`;
      ctx.arc(sh.x, sh.y, 1.4, 0, Math.PI * 2);
      ctx.fill();
    }

    rafId = requestAnimationFrame(tick);
  }

  // --- Visibilité onglet : pause automatique pour économiser CPU ---
  function onVisibilityChange() {
    if (document.hidden) {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = null;
    } else if (!rafId) {
      running = true;
      lastTime = performance.now();
      rafId = requestAnimationFrame(tick);
    }
  }

  // --- Init ---
  resize();
  window.addEventListener("resize", resize, { passive: true });
  document.addEventListener("visibilitychange", onVisibilityChange);
  rafId = requestAnimationFrame(tick);

  // --- API publique ---
  return {
    canvas,
    setOpacity(v) {
      options.opacity = v;
    },
    destroy() {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      stars = [];
      shootingStars.length = 0;
    },
  };
}
